class SocialFloating extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };

    this.querySelectorAll('[data-social-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!theme.config.mqlSmall) return;

    this.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    this.classList.add(this.classes.activeClass);

    // close promo-popup
    const popup = document.querySelector('promo-popup');
    popup && popup.close();
  }

  close() {
    this.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.classList.remove(this.classes.activeClass);
      this.classList.remove(this.classes.closingClass);
    }, 500);
  }
}
customElements.define('social-floating', SocialFloating);

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.cookieName = 'flowout:promo-popup';
    this.floatingCookieName = 'flowout:promo-floating';

    this.classes = {
      bodyClass: 'promo-popup--open',
      activeClass: 'is-active',
      closingClass: 'is-closing',
      showImage: 'show-image',
      floatingHidden: 'popup-floating--hidden'
    };

    this.popup = this.querySelector('.promo-popup');
    this.floating = this.querySelector('.popup-floating');

    // Hide floating widget if cookie exists
    if (this.floating && this.getCookie(this.floatingCookieName) && this.dataset.testMode !== 'true') {
      this.floating.classList.add(this.classes.floatingHidden);
    }

    /*
      Reopen after a native form POST so the visitor sees the result.

      Scoped to the server-rendered messages on purpose. The Klaviyo variant of
      this form always renders its success and error nodes, hidden via inline
      display:none, and querySelector matches hidden elements — so an unscoped
      '.form__message' matched on every page load. That opened the popup
      immediately on arrival, which made both the delay and the dismissal
      cookie below dead letters.
    */
    const postedMessage = this.querySelector(
      '.form__message:not([data-klaviyo-success]):not([data-klaviyo-error])'
    );
    if (postedMessage) {
      this.open();
      // Someone who just signed up shouldn't be asked again.
      if (postedMessage.classList.contains('newsletter-form__success')) this.suppress();
    }

    // A Klaviyo signup never reloads the page, so the form reports itself.
    this.addEventListener('newsletter:subscribed', () => this.suppress());

    this.querySelectorAll('[data-popup-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    // Floating widget close button
    this.querySelectorAll('[data-floating-close]').forEach((button) => {
      button.addEventListener('click', this.onFloatingCloseClick.bind(this));
    });

    if (!this.getCookie(this.cookieName) || this.dataset.testMode === 'true') {
      this.init();
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      document.body.classList.remove(this.classes.bodyClass);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  init() {
    if(!this.popup) {
      return;
    }
    
    if (Shopify && Shopify.designMode) {
      return;
    }

    setTimeout(function() {
      this.open();
    }.bind(this), parseInt(this.dataset.delay) * 1000);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.popup.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  /*
    Stop offering the popup for `expiry` days — used when the visitor has
    subscribed, where close() isn't guaranteed to run. Test mode keeps showing
    it so the popup stays reviewable.
  */
  suppress() {
    if (this.dataset.testMode === 'true') return;
    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  /*
    Open the popup and hand it to the shared overlay helper.

    It used to appear by class toggle alone: nothing announced it, nothing
    moved focus, and Escape did not dismiss it, so a screen-reader user was
    never told it was there and a keyboard user could not reach or close it
    (accessibility audit C-01).

    A centred popup covers the page, so it is a true modal — focus moves into
    it and is trapped. A corner popup leaves the page usable, so it is opened
    non-modally: it is announced and Escape-dismissible, but focus is left
    where the visitor put it rather than yanked away mid-task.
  */
  open() {
    document.body.classList.remove(this.classes.bodyClass);
    this.popup.classList.add(this.classes.activeClass);
    setTimeout(() => {
      this.popup.classList.add(this.classes.showImage);
    }, 600);

    const isCentered = this.popup.dataset.position === 'center';

    if (isCentered) {
      setScrollbarWidth();
      document.body.classList.add(this.classes.bodyClass);
    }

    // close social-floating
    const social = document.querySelector('social-floating');
    social && social.close();

    if (!window.theme || !theme.a11y) return;

    if (isCentered) {
      theme.a11y.openOverlay(this.popup, {
        focus: this.popup.querySelector('[data-popup-toggle]') || undefined,
        onEscape: () => this.close(),
      });
      return;
    }

    // Non-modal: announce the popup's heading so its arrival is not silent,
    // and still allow Escape to dismiss it.
    const heading = this.popup.querySelector('.popup__heading');
    theme.a11y.openOverlay(this.popup, {
      modal: false,
      focus: null,
      onEscape: () => this.close(),
      announce: heading ? heading.textContent.trim() : undefined,
    });
  }

  close() {
    this.popup.classList.add(this.classes.closingClass);

    if (window.theme && theme.a11y) theme.a11y.closeOverlay(this.popup);

    setTimeout(() => {
      this.popup.classList.remove(this.classes.activeClass);
      this.popup.classList.remove(this.classes.closingClass);
      this.popup.classList.remove(this.classes.showImage);

      if (this.popup.dataset.position === 'center') {
        document.body.classList.remove(this.classes.bodyClass);
      }
    }, 500);

    // Remove a cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0`;
  }

  onFloatingCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeFloating();
  }

  closeFloating() {
    if (!this.floating) return;

    this.floating.classList.add(this.classes.floatingHidden);

    // Remove cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.floatingCookieName);
      return;
    }

    this.setCookie(this.floatingCookieName, this.dataset.expiry);
  }
}
customElements.define('promo-popup', PromoPopup);
